import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { PUBLIC_ROLES } from '@/mocks/dealer';
import { ROLE_DEFINITIONS } from '@/lib/roles';
import type { Role } from '@/types/domain';

const GROUP_ICONS: Record<string, string> = {
  customer: 'ri-user-line',
  dealer: 'ri-store-2-line',
  partner: 'ri-briefcase-line',
  admin: 'ri-shield-star-line',
};

const inputClass =
  'w-full rounded-md border border-foreground-700 bg-foreground-900/70 px-4 py-3 text-sm text-background-50 placeholder:text-background-100/40 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

export default function Auth() {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage(t('auth.comingSoon'));
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-foreground-950 pb-20 pt-32 md:pt-40">
        <div className="mx-auto max-w-5xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <span className="mb-5 inline-block h-px w-12 bg-primary-500"></span>
              <h1 className="font-heading text-3xl leading-tight text-background-50 md:text-4xl">
                {t('auth.title')}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-background-100/70">
                {t('auth.subtitle')}
              </p>

              <form onSubmit={handleSubmit} className="mt-9 flex flex-col gap-5" noValidate>
                <div className="flex flex-col gap-2">
                  <label htmlFor="auth-email" className="text-sm font-medium text-background-100/80">
                    {t('auth.email')}
                  </label>
                  <input id="auth-email" type="email" required placeholder="ornek@email.com" className={inputClass} />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="auth-password" className="text-sm font-medium text-background-100/80">
                    {t('auth.password')}
                  </label>
                  <input id="auth-password" type="password" required placeholder="••••••••" className={inputClass} />
                </div>

                {message && (
                  <div className="flex items-start gap-2 rounded-md border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-300">
                    <i className="ri-information-line mt-0.5 text-lg" aria-hidden="true"></i>
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-8 py-3.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400 cursor-pointer"
                >
                  {t('auth.submit')}
                </button>

                <p className="flex items-start gap-2 text-xs leading-relaxed text-background-100/50">
                  <i className="ri-lock-line mt-0.5 text-base" aria-hidden="true"></i>
                  {t('auth.note')}
                </p>

                <Link
                  to="/bayilik"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-400 transition-colors hover:text-primary-300"
                >
                  <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden="true"></i>
                  {t('auth.applyCta')}
                </Link>
              </form>
            </div>

            <div className="rounded-lg border border-foreground-700/60 bg-foreground-900/50 p-7">
              <h2 className="font-heading text-xl text-background-50">{t('auth.roleInfoTitle')}</h2>
              <p className="mt-3 text-sm leading-relaxed text-background-100/60">
                {t('auth.roleInfoText')}
              </p>
              <div className="mt-6 flex flex-col gap-3">
                {PUBLIC_ROLES.map((role: Role) => {
                  const def = ROLE_DEFINITIONS[role];
                  return (
                    <div key={role} className="flex items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500/10 text-primary-400">
                        <i className={`${GROUP_ICONS[def.group]} text-base`} aria-hidden="true"></i>
                      </span>
                      <span className="text-sm text-background-100/85">{t(def.labelKey)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}